/**
 * System/system.js — Backend communication layer.
 * Provides request functions for the Node.js API (project/file management)
 * and Python/Flask API (data analysis). Automatically resolves localhost vs
 * production URLs based on the current browser location.
 * All POST requests use URL-encoded form data (not JSON).
 */
import axios from "axios";
import queryString from "query-string";
import store from "System/reduxStore";
// -------------------------------------------------------------
// -------------------------------------------------------------

// Detect local dev by checking if the hostname portion of the URL is "localhost".
const IS_LOCAL = window.location.href.slice(7, 16) === "localhost";

const backendHost = IS_LOCAL
  ? "http://localhost:3000"
  : (process.env.REACT_APP_NODE_API_URL || "https://quantaji.bademeister-jan.pro");

const pyHost = IS_LOCAL
  ? "http://localhost:8000"
  : (process.env.REACT_APP_PYTHON_API_URL || "https://quantajipy.bademeister-jan.pro");

/** Get the deal type prefix for Python API URLs. */
const getDealType = () => store.getState().project?.projectType || "receivables";

/** Build a prefixed Python API URL path. */
const pyUrl = (path) => `${pyHost}/${getDealType()}${path}`;

// -------------------------------------------------------------
// -------------------------------------------------------------
/** Send a POST request to the Node.js backend using the dynamic dispatch pattern: POST /{rubric}/{call}. */
export const backendNode = async (rubric, call, input) => {
  const requestData = {
    method: "post",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    url: backendHost + "/" + rubric + "/" + call,
    data: queryString.stringify(input),
  };
  const request = await axios(requestData);
  return request;
};

// -------------------------------------------------------------
// -------------------------------------------------------------
/** Upload a ZIP file to the Node.js backend with progress tracking. */
export const upload = async (formData, onProgress) => {
  const config = {
    onUploadProgress: (progressEvent) => {
      onProgress(
        Math.round((100 * progressEvent.loaded) / progressEvent.total)
      );
    },
  };
  return axios.post(pyUrl("/upload-zip"), formData, config);
};

// -------------------------------------------------------------
// -------------------------------------------------------------
/** Trigger browser download of the master CSV for a project. */
export const download = (projectid) => {
  let query = `?projectid=${encodeURI(projectid)}`;
  window.location.href = `${backendHost}/download/${query}`;
};

// -------------------------------------------------------------
// -------------------------------------------------------------
/** Trigger browser download of the filtered data frame CSV. */
export const downloadframe = (projectid) => {
  let query = `?projectid=${encodeURI(projectid)}`;
  window.location.href = `${backendHost}/downloadframe/${query}`;
};

// -------------------------------------------------------------
// -------------------------------------------------------------
/** Trigger browser download of the aging analysis CSV. */
export const downloadAging = (projectid) => {
  let query = `?projectid=${encodeURI(projectid)}`;
  window.location.href = `${backendHost}/download-aging/${query}`;
};

// -------------------------------------------------------------
// -------------------------------------------------------------
/** Trigger browser download of the full analysis report. */
export const downloadReport = (projectid) => {
  let query = `?projectid=${encodeURI(projectid)}`;
  window.location.href = `${backendHost}/download-report/${query}`;
};


// -------------------------------------------------------------
// -------------------------------------------------------------
/** Trigger browser download of an arbitrary file by server path and filename (legacy). */
export const downloadLegacyFile = (filepath, file) => {
  let query = `?filepath=${encodeURI(filepath)}&file=${encodeURI(file)}`;
  window.location.href = `${backendHost}/file${query}`;
};

// -------------------------------------------------------------
// -------------------------------------------------------------
/** Convert a local data path (containing "birddata/") to a backend-served static URL. */
export const getStaticURI = (path) => {
  const rhs = path.split("birddata/")[1];
  return `${backendHost}${rhs}`;
};


// -------------------------------------------------------------
// -------------------------------------------------------------
/** Poll the Python backend for the current status of a running analysis (e.g., Celery task progress). */
export const fetchAnalysisStatus = async (projectid) => {
  const result = await axios.get(pyUrl(`/analysis-status?projectid=${encodeURI(projectid)}`));
  return result.data;
};

/** Trigger browser download of all Parquet files for a project from the Python backend. */
export const downloadParquets = (projectid) => {
  window.location.href = pyUrl(`/download-parquets?projectid=${encodeURI(projectid)}`);
};

/** Download a single analysis file in a given format (parquet, csv, xlsx). */
export const downloadFile = async (projectid, filename, format) => {
  const params = new URLSearchParams({ projectid, filename, format });
  const response = await fetch(pyUrl(`/download-file?${params}`));
  if (!response.ok) throw new Error("Download failed");
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const basename = filename.replace(".parquet.gzip", "");
  const ext = format === "parquet" ? "parquet.gzip" : format;
  const a = document.createElement("a");
  a.href = url;
  a.download = `${basename}.${ext}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/** Trigger browser download of all CSV files for a project. */
export const downloadCsvs = async (projectid) => {
  const response = await fetch(pyUrl(`/download-csvs?projectid=${encodeURIComponent(projectid)}`));
  if (!response.ok) throw new Error("CSV download failed");
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${projectid}_data_csv.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/** Trigger browser download of all analysis data as a single XLSX workbook. */
export const downloadXlsx = async (projectid) => {
  const response = await fetch(pyUrl(`/download-xlsx?projectid=${encodeURIComponent(projectid)}`));
  if (!response.ok) throw new Error("XLSX download failed");
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${projectid}_analysis.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/** Download the executive report as a PowerPoint presentation via POST. */
export const downloadPptx = async (projectid, reportData) => {
  const formData = new FormData();
  formData.append("projectid", projectid);
  formData.append("report_json", JSON.stringify(reportData));

  const response = await fetch(pyUrl("/download-pptx"), {
    method: "POST",
    body: formData,
  });

  if (!response.ok) throw new Error("PPTX generation failed");

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${projectid}_report.pptx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// -------------------------------------------------------------
// -------------------------------------------------------------
/** Send a POST request to the Python/Flask backend using the dynamic dispatch pattern: POST /{deal_type}/{rubric}/{call}. Timeout disabled (0) for long-running computations. */
export const backendPy = (rubric, call, input) => {
  const dealType = getDealType();
  const requestData = {
    method: "post",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    url: `${pyHost}/${dealType}/${rubric}/${call}`,
    data: queryString.stringify(input),
    timeout: 0
  };
  const request = axios(requestData);
  return request;
};
